import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pprApi, type PprJob } from '@/lib/apiClient';
import { fmtDate } from '../pprShared';
import { daysFromToday } from '@/lib/dates';
import { Button, Modal } from '@/components';

/**
 * Cancel / reopen / delete a PPR (issue #340).
 *
 * Before this, a review started by mistake could not be undone from the app at
 * all — no endpoint, no service method, no control. #314 had to be fixed by
 * deleting rows by hand.
 *
 * Two paths, because "remove" means two different things:
 *   • CANCEL — the general case. Terminal status, keeps the record of who was
 *     notified and what came back, and re-arms the project's closeout alert.
 *     Reversible via Reopen.
 *   • DELETE — offered only while the review is still Initiating with nobody
 *     notified, which is the genuine fat-finger case. Under those conditions
 *     there is nothing to preserve. The API enforces the same two conditions, so
 *     hiding the button is a courtesy, not the gate.
 */

// pprc- prefix: these classes live only in this component. Reusing a name from
// PprJobDetail's stylesheet would render unstyled here and fail silently.
const css = `
  .pprc-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-4);
  }
  .pprc-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ew-s-3);
    padding: 10px 14px;
    margin-bottom: var(--ew-s-4);
    background: #F0EAE8;
    color: #7A5C55;
    border: 1px solid rgba(122, 92, 85, 0.3);
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .pprc-banner-text { margin-right: auto; }
  .pprc-link {
    background: none;
    border: none;
    padding: 0;
    color: #8F2414;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    cursor: pointer;
  }
  .pprc-link:hover { text-decoration: underline; }
  .pprc-link:disabled { opacity: 0.5; cursor: default; text-decoration: none; }

  .pprc-field { display: flex; flex-direction: column; gap: 6px; }
  .pprc-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    color: var(--ew-n-600);
  }
  .pprc-textarea {
    width: 100%;
    min-height: 90px;
    padding: 10px 12px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    color: var(--ew-dark);
    background: #fff;
    resize: vertical;
    box-sizing: border-box;
  }
  .pprc-body p {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    color: var(--ew-dark);
    margin: 0 0 var(--ew-s-3) 0;
  }
  .pprc-note {
    padding: 10px 12px;
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.5 var(--ew-font);
    margin-bottom: var(--ew-s-3);
  }
  .pprc-note-info  { background: #E1EEF7; color: #0D4C7C; }
  .pprc-note-warn  { background: #FEF3C7; color: #92400E; }
  .pprc-error {
    margin-top: var(--ew-s-2);
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1.4 var(--ew-font);
    color: #8F2414;
  }
  .pprc-stat {
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
    color: var(--ew-n-600);
  }
`;

/**
 * What happens to the closeout alert when this review is cancelled or deleted.
 *
 * Cancelling clears the project's ppr_closeout_alerts row so the coordinator can
 * be told again as the job nears completion. But that scan only looks 0–7 days
 * FORWARD from today, so if the estimated completion has already gone by, the
 * reset cannot produce anything. Saying so at cancel time is the difference
 * between the coordinator tracking that job by hand and assuming it will come
 * back on its own (product owner, 2026-08-17).
 */
function closeoutOutlook(job: PprJob): { tone: 'info' | 'warn'; text: string } {
  if (!job.projectId) {
    return {
      tone: 'info',
      text: 'This review is not linked to a tracked project, so there is no closeout alert to reset.',
    };
  }
  // ⚠️ The scan excludes whole categories of project regardless of date, and a
  // PPR on one is perfectly normal — the Start PPR picker includes closed jobs
  // deliberately. The API decides this (CLOSEOUT_BLOCK_SUBQUERY) so the rule
  // lives with getCloseoutDue's own filters rather than being re-derived here.
  if (job.projectCloseoutBlock) {
    // The sibling-review case reads differently from the project-level ones: it
    // isn't a warning about this job so much as a note that another review is
    // already holding the project's place in the pipeline.
    if (job.projectCloseoutBlock === 'other-review') {
      return {
        tone: 'info',
        text: 'This project has another review that is not cancelled, so it stays out of '
          + 'the closeout scan and no new alert will fire. Nothing is lost — that other '
          + 'review is the live one.',
      };
    }
    const why = {
      archived: 'This project is archived',
      deleted: 'This project has been deleted',
      'sub-project': 'This is a maintenance sub-project',
    }[job.projectCloseoutBlock] ?? 'This project is excluded';
    return {
      tone: 'warn',
      text: `${why}, and the closeout scan skips it — so no alert will fire whether or not `
        + 'it is reset. Track this job for closeout yourself.',
    };
  }
  // The EFFECTIVE completion (#414) — the later of the project's estimated
  // completion and its latest mobilization end, which is the date getCloseoutDue
  // actually windows on. The copy below says "completion date" rather than
  // "estimated completion" for that reason: on a remobbed job this IS the mob
  // end, and naming it as the estimate would point the coordinator at a column
  // that disagrees.
  const raw = job.projectEffectiveCompletion;
  // daysFromToday, not `new Date(raw) < new Date()`: it is a calendar date the
  // API serializes as UTC midnight, so comparing it against a local "now"
  // reports today's date as already past all evening in Eastern.
  const days = daysFromToday(raw);
  if (days === null) {
    return {
      tone: 'warn',
      text: 'The project has no completion date, so no closeout alert can fire for it. '
        + 'Set one on the project, or track this job for closeout yourself.',
    };
  }
  if (days < 0) {
    return {
      tone: 'warn',
      text: `The project's completion date (${fmtDate(raw)}) has already passed, so the closeout `
        + 'alert will NOT fire again even though it has been reset. Track this job for closeout yourself, '
        + 'or move the date out on the project.',
    };
  }
  return {
    tone: 'info',
    text: `The closeout alert will be reset, so you will be told again as the project nears its `
      + `completion date (${fmtDate(raw)}).`,
  };
}

export default function CancelReviewActions({ job }: { job: PprJob }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'cancel' | 'delete' | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setMode(null);
    setReason('');
    setError(null);
  };

  const cancelMut = useMutation({
    mutationFn: () => pprApi.cancelJob(job.id, reason.trim()),
    onSuccess: () => {
      close();
      qc.invalidateQueries({ queryKey: ['ppr'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const reopenMut = useMutation({
    mutationFn: () => pprApi.reopenJob(job.id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['ppr'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => pprApi.deleteJob(job.id),
    onSuccess: () => {
      close();
      // The job this page is showing no longer exists — leave before the detail
      // query refetches it and renders a 404.
      qc.invalidateQueries({ queryKey: ['ppr'] });
      navigate('/ppr');
    },
    onError: (err: Error) => setError(err.message),
  });

  // ─── Already cancelled: offer the way back ────────────────────────────────
  //
  // The reason, dates and actors are NOT repeated here — CancellationRecord
  // renders those for every viewer, including read-only ones who cannot see this
  // component at all. This is just the control.
  if (job.status === 'Cancelled') {
    return (
      <>
        <style>{css}</style>
        <div className="pprc-banner">
          <span className="pprc-banner-text">
            Reopening returns this review to Initiating — the nine contacts keep
            whatever was already notified and answered.
          </span>
          <Button
            variant="secondary"
            size="sm"
            isLoading={reopenMut.isPending}
            onClick={() => reopenMut.mutate()}
          >
            Reopen review
          </Button>
        </div>
        {error && <p className="pprc-error">{error}</p>}
      </>
    );
  }

  // Mirrors PprService.remove: Initiating, nobody notified, AND nothing answered.
  //
  // ⚠️ RESPONSES ARE A SEPARATE TEST. A Formstack submission can arrive before
  // its contact is marked notified, leaving contactsNotified = 0 while
  // contactsResponded > 0. Keying off notified alone offered Delete on a review
  // holding a real response — the service guard refuses it, so the only outcome
  // was a confirm dialog claiming no response could exist followed by an
  // avoidable API error.
  const notified = job.contactsNotified ?? 0;
  const responded = job.contactsResponded ?? 0;
  const canDelete = job.status === 'Initiating' && notified === 0 && responded === 0;
  const outlook = closeoutOutlook(job);

  return (
    <>
      <style>{css}</style>

      <div className="pprc-actions">
        <button type="button" className="pprc-link" onClick={() => setMode('cancel')}>
          Cancel review
        </button>
        {canDelete && (
          <button type="button" className="pprc-link" onClick={() => setMode('delete')}>
            Delete review
          </button>
        )}
        {!canDelete && job.status === 'Initiating' && (
          <span className="pprc-stat">
            {responded > 0
              ? `${responded} survey response${responded === 1 ? '' : 's'} already received`
              : `${notified} of 9 contacts notified`}
            {' '}— cancel keeps that record; delete is no longer offered.
          </span>
        )}
      </div>

      <Modal
        isOpen={mode === 'cancel'}
        onClose={close}
        title="Cancel this review?"
        footer={
          <>
            <Button variant="secondary" onClick={close}>Keep it</Button>
            <Button
              variant="danger"
              isLoading={cancelMut.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => cancelMut.mutate()}
            >
              Cancel review
            </Button>
          </>
        }
      >
        <div className="pprc-body">
          <p>
            <strong>{job.jobName}</strong> (#{job.jobNumber}) moves to Cancelled and leaves
            the pipeline. Nothing is deleted: the {notified} notified contact
            {notified === 1 ? '' : 's'}, any survey responses already in, and the
            financial data all stay on the record. You can reopen it later.
          </p>
          <div className={`pprc-note pprc-note-${outlook.tone}`}>{outlook.text}</div>
          <div className="pprc-field">
            <label htmlFor="pprc-reason">Reason for cancelling (required)</label>
            <textarea
              id="pprc-reason"
              className="pprc-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Started on the wrong job number — the correct review is on 10931."
            />
          </div>
          {error && <p className="pprc-error">{error}</p>}
        </div>
      </Modal>

      <Modal
        isOpen={mode === 'delete'}
        onClose={close}
        title="Delete this review?"
        footer={
          <>
            <Button variant="secondary" onClick={close}>Keep it</Button>
            <Button
              variant="danger"
              isLoading={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <div className="pprc-body">
          <p>
            <strong>{job.jobName}</strong> (#{job.jobNumber}) and its nine contact rows are
            removed for good. This cannot be undone.
          </p>
          <div className="pprc-note pprc-note-info">
            Offered because this review is still at Initiating, no contact has been
            notified, and no survey response has come in — so there is nothing on it
            to lose. Cancel instead if you would rather keep the record that it was
            opened.
          </div>
          <div className={`pprc-note pprc-note-${outlook.tone}`}>{outlook.text}</div>
          {error && <p className="pprc-error">{error}</p>}
        </div>
      </Modal>
    </>
  );
}
